import { useAuth } from "@/lib/AuthContext";
import { useCurrentUrl } from "@/lib/useCurrentPath";
import {
  mixpanelService,
  type MixpanelSearchScope,
} from "@/services/mixpanel.service";
import {
  logSearchClickToSupabase,
  logSearchToSupabase,
  type SearchEventScope,
} from "@/supabase/services/searchEvents";
import { useCallback, useEffect, useMemo, useRef } from "react";

export type TrackedSearchResult = {
  id: number | string;
  type: "course" | "lesson" | "collection";
  slug?: string | null;
  title?: string | null;
  courseSlug?: string | null;
};

type UseSearchTrackingOptions = {
  scope: SearchEventScope;
  /** In-course search context. */
  courseId?: number | null;
  courseSlug?: string | null;
  courseTitle?: string | null;
};

const SCOPE_LABELS: Record<SearchEventScope, MixpanelSearchScope> = {
  global: "Global",
  courses_hub: "Courses Hub",
  in_course: "In Course",
};

/**
 * Fires a "Performed Search" only after the query has been idle for a beat, so
 * type-ahead prefixes ("voi", "voici", …) collapse into the final query. A
 * result click flushes any pending search first, guaranteeing every click has
 * a matching search event, then logs the click. Both go to Mixpanel and to
 * the `search_events` table.
 */
export function useSearchTracking({
  scope,
  courseId,
  courseSlug,
  courseTitle,
}: UseSearchTrackingOptions) {
  const { user } = useAuth();
  const { fullPath } = useCurrentUrl();

  const pendingRef = useRef<{
    query: string;
    results: TrackedSearchResult[];
  } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastLoggedQueryRef = useRef<string | null>(null);

  // Keep latest values available to the unmount flush without re-subscribing.
  const contextRef = useRef({ user, fullPath, courseId, courseSlug, courseTitle });
  contextRef.current = { user, fullPath, courseId, courseSlug, courseTitle };

  const flushPending = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    lastLoggedQueryRef.current = pending.query;

    const ctx = contextRef.current;
    void mixpanelService.trackSearchPerformed(
      SCOPE_LABELS[scope],
      pending.query,
      pending.results.length,
      ctx.user,
      ctx.fullPath,
      ctx.courseSlug,
      ctx.courseTitle,
    );
    logSearchToSupabase(scope, pending.query, pending.results, ctx.courseId);
  }, [scope]);

  const SEARCH_TRACK_IDLE_MS = 1500;

  /** Call each time a search settles (results rendered for a query). */
  const onResultsSettled = useCallback(
    (query: string, results: TrackedSearchResult[]) => {
      const q = query.trim();
      if (!q) return;
      if (lastLoggedQueryRef.current === q && !pendingRef.current) return;
      if (pendingRef.current?.query === q) {
        pendingRef.current = { query: q, results };
        return;
      }
      pendingRef.current = { query: q, results };
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flushPending, SEARCH_TRACK_IDLE_MS);
    },
    [flushPending],
  );

  /** Call from the result press handler. `position` is 1-based. */
  const trackResultClick = useCallback(
    (query: string, result: TrackedSearchResult, position: number) => {
      flushPending();
      const ctx = contextRef.current;
      void mixpanelService.trackSearchResultClick(
        SCOPE_LABELS[scope],
        query.trim(),
        {
          type: result.type,
          slug: result.slug,
          title: result.title,
          courseSlug: result.courseSlug ?? ctx.courseSlug,
          position,
        },
        ctx.user,
        ctx.fullPath,
      );
      logSearchClickToSupabase(scope, query.trim(), result, position, ctx.courseId);
    },
    [scope, flushPending],
  );

  // Don't lose the last search when the panel/screen unmounts mid-idle.
  useEffect(() => flushPending, [flushPending]);

  return useMemo(
    () => ({ onResultsSettled, trackResultClick }),
    [onResultsSettled, trackResultClick],
  );
}
